/*
 * ls-stade-motion-hero — the "Tous au stade" hero motion on the NATIVE aiconeo
 * section (p70). Reference choreography: assets/ls-stade-hero.js (the Liquid
 * hero it replaces). Content is the builder's; this file only animates it.
 *
 * Structure recognised (element TYPE + order, never text/ids):
 *   first image WITH children (.content-type-neo-image--overlay) = photo/poster
 *     └ content layer: a native video (html5), the h1 with ONE <em>, buttons
 *   - word A = the nodes of the h1 before the <em>, word B = the <em> and what
 *     follows. They are wrapped at runtime in span.ls-hero__word > span
 *     .ls-hero__word-inner (the text nodes are MOVED, never rewritten).
 *   - the video block is re-parented behind the scrim as the background layer:
 *     muted, looping, playsinline autoplay, no controls, aria-hidden; the photo
 *     becomes its poster and the poster is dropped once it actually plays.
 *   - any button of the hero whose href is "#<id>" (the 2nd one,
 *     "#section-<next uuid>") gets a smooth, header-aware scroll. Without JS
 *     the plain anchor jumps to the same place (after the pin spacer).
 * Anything else (no photo-with-children, no video, no h1/<em>, no text before
 * the <em>) → bail, the native page stays untouched.
 *
 * Motion (as the original):
 *   ≥750px  the section PINS where it rests (under the fixed header; the
 *           original pinned at 'top top', leaving a strip of the next
 *           section below it) for +=70% of the viewport; word A starts 0.62em
 *           high and scrub-descends onto word B's line (0.85 of the pin); the
 *           photo/video pans yPercent -4 → +4 across the whole pin.
 *   <750px  no pin, no scrub (the title wraps to two lines; there is no line
 *           to merge onto) — video + entrance only; the hero height uses dvh.
 *   both    one-shot entrance: the word inners rise in, the buttons fade up.
 * Resting frame = the merged line (what the builder shows).
 *
 * Reduced motion: no pin/scrub/entrance/autoplay. The video is taken out of
 * the content flow and hidden (preload none, paused), so the hero reads like
 * the original's reduced frame — the full-bleed photo with the merged title —
 * instead of a small player or an autoplaying loop. The page chrome
 * (transparent navbar) still applies; it is layout, not motion.
 *
 * Title fit: at ≥750px the staff font size is the MAXIMUM; it is scaled down
 * (inline) only when "A B" would not fit on one line, which the merge needs.
 * Re-fitted on every ScrollTrigger refresh (resize, fonts, load).
 */
(function () {
  'use strict';
  if (!window.LsStadeMotion) return;

  var WORD_A_START = -0.62; /* em — Figma TOUS 124px above the line at 200px */
  var MERGE_PORTION = 0.85;
  var PIN_DISTANCE = '+=70%';
  var MEDIA_FROM = -4;
  var MEDIA_TO = 4;
  var DESKTOP = '(min-width: 750px)';

  function headerOffset() {
    var header = document.querySelector('.shopify-section-header, .section-header');
    return header ? Math.round(header.getBoundingClientRect().height) : 0;
  }

  /** Wrap nodes[] in span.ls-hero__word > span.ls-hero__word-inner, in place. */
  function wrapWord(nodes, key) {
    var outer = document.createElement('span');
    var inner = document.createElement('span');
    outer.className = 'ls-hero__word ls-hero__word--' + key;
    outer.setAttribute('data-ls-hero-word', key);
    inner.className = 'ls-hero__word-inner';
    outer.appendChild(inner);
    nodes[0].parentNode.insertBefore(outer, nodes[0]);
    nodes.forEach(function (n) { inner.appendChild(n); });
    return outer;
  }

  LsStadeMotion.scene('hero', function (section, ctx) {
    // ---- recognise (nothing is touched before every check passed) ----------
    var cover = ctx.q('.content-type-neo-image--overlay');
    if (!cover) return null;
    var layer = cover.querySelector(':scope > .neo-image__overlay');
    var photo = cover.querySelector(':scope > img');
    var videoBlock = layer && layer.querySelector(':scope > .content-type-neo-video');
    var video = videoBlock && videoBlock.querySelector('video');
    var h1 = layer && layer.querySelector('.content-type-neo-heading h1');
    var em = h1 && h1.querySelector(':scope > em');
    if (!layer || !video || !h1 || !em || !em.textContent.trim()) return null;
    var before = [];
    var after = [];
    var seen = false;
    Array.prototype.forEach.call(h1.childNodes, function (n) {
      if (n === em) { seen = true; return; }
      (seen ? after : before).push(n);
    });
    if (!before.map(function (n) { return n.textContent; }).join('').trim()) return null;
    var buttons = ctx.items('button').filter(function (b) { return layer.contains(b); });

    // ---- apply ---------------------------------------------------------------
    var reduced = ctx.reduced;
    var wordA = wrapWord(before, 'a');
    var wordB = wrapWord([em].concat(after), 'b');
    var inners = [wordA.firstChild, wordB.firstChild];
    cover.classList.add('ls-hero__cover');
    h1.classList.add('ls-hero__title');

    // Video: out of the content flow, behind the scrim (photo < video < scrim < layer).
    var scrim = cover.querySelector(':scope > .neo-image__scrim');
    cover.insertBefore(videoBlock, scrim || layer);
    videoBlock.classList.add('ls-hero__video');
    videoBlock.setAttribute('aria-hidden', 'true');
    video.removeAttribute('controls');
    video.controls = false;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('tabindex', '-1');
    video.playsInline = true;
    video.loop = true;
    if (photo && photo.getAttribute('src')) video.setAttribute('poster', photo.currentSrc || photo.getAttribute('src'));
    if (photo) photo.loading = 'eager';

    if (reduced) {
      video.removeAttribute('autoplay');
      video.setAttribute('preload', 'none');
      video.pause();
      section.classList.add('ls-hero--still');
    } else {
      video.setAttribute('autoplay', '');
      video.setAttribute('preload', 'auto');
      video.addEventListener('playing', function () { video.removeAttribute('poster'); }, { once: true });
      video.addEventListener('ended', function () {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      });
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {}); // autoplay refused → the photo stays
    }

    section.classList.add('ls-hero--on');
    // Page chrome (transparent navbar, ls-stade-motion-hero.css) only when the hero opens the page.
    if (document.querySelector('#aico-content .aico-neo-section') === section) section.classList.add('ls-hero--top');

    // ---- layout that follows the viewport --------------------------------------
    var heroTop = 0;
    function measureTop() {
      var top = Math.round(section.getBoundingClientRect().top + window.pageYOffset);
      if (top >= 0 && top < window.innerHeight) {
        heroTop = top;
        section.style.setProperty('--ls-stade-hero-top', top + 'px');
      }
    }

    // The staff size is the renderer's INLINE font-size (+ tier style tags):
    // keep it and restore it before every fit, never just remove the property.
    var staffInline = h1.style.getPropertyValue('font-size');
    var staffPrio = h1.style.getPropertyPriority('font-size');
    function fitTitle() {
      if (staffInline) h1.style.setProperty('font-size', staffInline, staffPrio);
      else h1.style.removeProperty('font-size');
      if (!window.matchMedia(DESKTOP).matches) return;
      var staff = parseFloat(getComputedStyle(h1).fontSize);
      if (!staff) return;
      var cs = getComputedStyle(layer);
      var hs = getComputedStyle(h1);
      var avail = layer.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) -
        parseFloat(hs.marginLeft) - parseFloat(hs.marginRight);
      h1.classList.add('ls-hero__title--measure');
      var need = h1.scrollWidth;
      h1.classList.remove('ls-hero__title--measure');
      if (avail > 0 && need > avail) {
        h1.style.setProperty('font-size', Math.floor(staff * (avail / need) * 0.98) + 'px', 'important');
      }
    }

    function layout() {
      // Pinned: the section is position:fixed, its top is not the document top.
      if (!section.parentNode.classList.contains('pin-spacer') || window.pageYOffset === 0) measureTop();
      fitTitle();
    }
    layout();
    ScrollTrigger.addEventListener('refreshInit', layout);

    // ---- in-page links: smooth, header-aware scroll ---------------------------
    buttons.forEach(function (b) {
      var a = b.querySelector('a[href^="#"]');
      if (!a) return;
      var id = decodeURIComponent(a.getAttribute('href').slice(1));
      if (!id) return;
      a.addEventListener('click', function (event) {
        var target = document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
        window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: reduced ? 'auto' : 'smooth' });
      });
    });

    if (reduced) return null;

    // ---- pinned merge (≥750px) -----------------------------------------------
    var media = [videoBlock].concat(photo ? [photo] : []);
    function emPx() { return parseFloat(getComputedStyle(h1).fontSize) || 0; }
    if (ctx.mm) {
      ctx.mm.add(DESKTOP, function () {
        section.classList.add('ls-hero--pinned');
        var tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            // Pinned where it rests (under the fixed header / INEOS strip), so the
            // fold-fitted hero holds full-height with nothing peeking below it.
            start: function () { return 'top ' + heroTop + 'px'; },
            end: PIN_DISTANCE,
            scrub: 0.6,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true
          }
        });
        tl.fromTo(wordA, { y: function () { return WORD_A_START * emPx(); } }, { y: 0, ease: 'none', duration: MERGE_PORTION }, 0);
        tl.fromTo(media, { yPercent: MEDIA_FROM }, { yPercent: MEDIA_TO, ease: 'none', duration: 1 }, 0);
        return function () { section.classList.remove('ls-hero--pinned'); };
      });
    }

    // ---- entrance (once per page view) -----------------------------------------
    if (section.getBoundingClientRect().bottom > 0) {
      var entrance = gsap.timeline({ delay: 0.4 });
      entrance.from(inners, { yPercent: 110, autoAlpha: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out' }, 0);
      // The button wrappers, not the <a>: the renderer puts a transform transition on the anchors.
      if (buttons.length) entrance.from(buttons, { y: 24, autoAlpha: 0, duration: 0.5, stagger: 0.08, ease: 'power3.out' }, 0.45);
    }

    return { refresh: layout };
  });
})();
